import TutorialTopicoService from "@/service/TutorialTopicoService";
import { usePagination } from "@/composables/usePagination";
import { ref } from "vue";

export function useTutoriais() {
  const tutoriais = ref([]);
  const tutorial = ref(null);
  const isLoading = ref(false);
  const isSaving = ref(false);

  const pagination = usePagination({
    initialPage: 1,
    pageSize: 10,
    baseIndex: 1,
  });

  const fetchAllTutoriais = async () => {
    isLoading.value = true;
    try {
      const response = await TutorialTopicoService.getAllTutorialTopico();
      tutoriais.value = response?.data || response || [];
    } catch (error) {
      console.error("Error fetching tutoriais:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTutorialById = async (id) => {
    isLoading.value = true;
    try {
      const response = await TutorialTopicoService.getTutorialTopicoById(id);
      tutorial.value = response;
    } catch (error) {
      console.error("Error fetching tutorial by id:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTutoriaisPaginado = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value
  ) => {
    isLoading.value = true;
    try {
      const response =
        await TutorialTopicoService.getTutorialTopicoTopicoPaginado(page, size);
      tutoriais.value = response?.data || response || [];
      pagination.setPaginationInfo(response);
      pagination.currentPage.value = page;
    } catch (error) {
      console.error("Error fetching paginated tutoriais:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTutoriaisByTopicoId = async (topicoId) => {
    isLoading.value = true;
    try {
      const response =
        await TutorialTopicoService.getTutorialTopicoByTopicoId(topicoId);
      tutoriais.value = response || [];
    } catch (error) {
      console.error("Error fetching tutoriais by topico id:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTutoriaisByTopicoIdPaginado = async (
    topicoId,
    page = pagination.currentPage.value,
    size = pagination.pageSize.value
  ) => {
    isLoading.value = true;
    try {
      const response =
        await TutorialTopicoService.getTutorialTopicoByTopicoIdPaginado(
          topicoId,
          page,
          size
        );
      tutoriais.value = response?.data || response || [];
      pagination.setPaginationInfo(response);
      pagination.currentPage.value = page;
    } catch (error) {
      console.error("Error fetching paginated tutoriais by topico id:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveTutorial = async (tutorialData) => {
    isSaving.value = true;
    try {
      await TutorialTopicoService.createTutorialTopico(tutorialData);
      await fetchAllTutoriais();
    } catch (error) {
      console.error("Error saving tutorial:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const updateTutorial = async (id, tutorialData) => {
    isSaving.value = true;
    try {
      await TutorialTopicoService.updateTutorialTopico(id, tutorialData);
      await fetchAllTutoriais();
    } catch (error) {
      console.error("Error updating tutorial:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const deleteTutorial = async (id) => {
    isLoading.value = true;
    try {
      await TutorialTopicoService.deleteTutorialTopico(id);
      await fetchAllTutoriais();
    } catch (error) {
      console.error("Error deleting tutorial:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const nextPage = () => pagination.nextPage(fetchTutoriaisPaginado);
  const previousPage = () => pagination.previousPage(fetchTutoriaisPaginado);
  const goToPage = (page) => pagination.goToPage(page, fetchTutoriaisPaginado);

  return {
    tutoriais,
    tutorial,
    isLoadingTutorial: isLoading,
    isSavingTutorial: isSaving,
    totalTutoriais: pagination.totalElements,
    currentPage: pagination.currentPage,
    totalPages: pagination.totalPages,
    pageSize: pagination.pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    fetchAllTutoriais,
    fetchTutorialById,
    fetchTutoriaisPaginado,
    saveTutorial,
    updateTutorial,
    deleteTutorial,
    fetchTutoriaisByTopicoId,
    fetchTutoriaisByTopicoIdPaginado,
    nextPage,
    previousPage,
    goToPage,
  };
}
